import { apiClient } from "../../lib/http/apiClient";
import type {
  Campaign,
  CampaignAnalytics,
  CampaignCreatePayload,
  CampaignInsights,
  TopupInitiateResponse,
  Wallet,
} from "./types";

const BASE = "/api/v1/boost";

export const boostService = {
  getWallet: async (): Promise<Wallet> => {
    const { data } = await apiClient.get(`${BASE}/wallet`);
    return data;
  },

  topupInitiate: async (amount_credits: number, pending_campaign?: Record<string, unknown>, gateway?: string): Promise<TopupInitiateResponse> => {
    const { data } = await apiClient.post(`${BASE}/wallet/topup`, { amount_credits, pending_campaign, ...(gateway ? { gateway } : {}) });
    return data;
  },

  getPaymentOptions: async (): Promise<{ enabled: string[]; default: string | null; show_picker: boolean }> => {
    const { data } = await apiClient.get(`/api/v1/payments/options`);
    return data;
  },

  topupConfirm: async (payload: {
    order_id: string;
    payment_id: string;
    signature: string;
  }): Promise<Wallet> => {
    const { data } = await apiClient.post(`${BASE}/wallet/topup/confirm`, payload);
    return data;
  },

  processPendingCampaign: async (stripeSessionId: string): Promise<{ ok: boolean; campaign_id?: string; reason?: string }> => {
    const { data } = await apiClient.post(`${BASE}/wallet/topup/process-pending`, null, { params: { stripe_session_id: stripeSessionId } });
    return data;
  },

  topupStatus: async (orderId: string, gateway?: string): Promise<{
    order_id: string;
    gateway: string;
    status: string;
    settled: boolean;
    amount_paise: number;
    amount_credits: number;
    currency: string;
    balance_paise: number;
    balance_credits: number;
  }> => {
    const { data } = await apiClient.get(`${BASE}/wallet/topup/${orderId}/status`, {
      params: gateway ? { gateway } : undefined,
    });
    return data;
  },

  getCampaigns: async (): Promise<Campaign[]> => {
    const { data } = await apiClient.get(`${BASE}/campaigns`);
    return data;
  },

  getCampaign: async (id: string): Promise<Campaign> => {
    const { data } = await apiClient.get(`${BASE}/campaigns/${id}`);
    return data;
  },

  createCampaign: async (payload: CampaignCreatePayload): Promise<Campaign> => {
    const { data } = await apiClient.post(`${BASE}/campaigns`, payload);
    return data;
  },

  patchCampaign: async (
    id: string,
    action: "pause" | "resume" | "cancel"
  ): Promise<Campaign> => {
    const { data } = await apiClient.patch(`${BASE}/campaigns/${id}`, { action });
    return data;
  },

  recordImpression: async (payload: {
    event_id: string;
    campaign_id: string;
    deal_id: string;
    event_type: "impression";
    surface: "search" | "browse";
    position: number;
    session_id?: string;
  }): Promise<void> => {
    await apiClient.post(`${BASE}/events`, payload);
  },

  recordEvent: async (payload: {
    event_id: string;
    campaign_id: string;
    deal_id: string;
    event_type: "impression" | "click" | "search_appearance";
    surface: "search" | "browse";
    position: number;
    session_id?: string;
  }): Promise<void> => {
    await apiClient.post(`${BASE}/events`, payload);
  },

  getAnalytics: async (campaignId: string): Promise<CampaignAnalytics> => {
    const { data } = await apiClient.get(`${BASE}/campaigns/${campaignId}/analytics`);
    return data;
  },

  getInsights: async (campaignId: string): Promise<CampaignInsights> => {
    const { data } = await apiClient.get(`${BASE}/campaigns/${campaignId}/insights`);
    return data;
  },
};
